import type { ReactNode } from 'react';
import { useEffect, useRef } from 'react';

import { Dialog, IconButton, Tooltip, Typography } from '@mui/material';
import { CloseIcon } from '@truvhq/ui';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { useLocation } from 'react-router-dom';

import { useOnboardingState } from 'entities/onboarding/api/useOnboardingState';
import { useAssistantEnabled } from 'features/onboarding/lib/useAssistantEnabled';
import { useAutoOpenOnboarding } from 'features/onboarding/lib/useAutoOpenOnboarding';
import { useAssistantPanelTitle } from 'features/onboarding/lib/useOnboardingChecklist';
import { useOnboardingDialogStore } from 'features/onboarding/lib/useOnboardingDialogStore';
import { ChecklistPane } from 'features/onboarding/ui/ChecklistPane';
import { OnboardingConversation } from 'features/onboarding/ui/OnboardingConversation';
import { PAGE_BANNERS_HEIGHT_VAR } from 'shared/lib/layout/usePageBannersHeight';
import { useScreenSize } from 'shared/lib/screen/useScreenSize';
import { SwipeSheet } from 'shared/ui/SwipeSheet';

import ChevronDoubleRightIcon from './icons/chevron-double-right.svg?react';

// The panel is the assistant with or without a checklist in it, so the control that dismisses it
// says so regardless of what the title currently reads.
const HIDE_LABEL = 'Hide chat';
// The design's easing, shared with the page-width transition in app/styles/style.css so the panel
// and the content it displaces move on one curve.
const SLIDE = { duration: 0.24, ease: [0.22, 0.7, 0.3, 1] } as const;

// Mounts the conversational onboarding as a global popup driven by useOnboardingDialogStore.
// Opening it automatically the first time is useAutoOpenOnboarding's job; after that the user opens
// it manually via the launcher, or the nav drawer's "Ask Truv AI" on a phone.
//
// This is mounted in App.tsx outside AuthGuard, so it relies on useAssistantEnabled to check app
// access — otherwise it renders over the "Account pending" screen and calls endpoints that answer
// 403 for those users.
//
// Gated out here rather than inside the body, as ChatIntegration does for Inkeep: the checklist
// hooks below fetch the onboarding state without an `enabled` of their own, so a body that bails on
// its last line would still have made the request.
export function OnboardingDialog() {
  return useAssistantEnabled() ? <OnboardingDialogContent /> : null;
}

/**
 * Leaves the sheet behind when the user follows a step link.
 *
 * Only on a phone: there the sheet covers the page, so navigating without this lands the user on
 * the target page with the chat still over it. A desktop panel leaves the page visible beside it,
 * and closing it there would throw away a conversation the user can still see.
 */
function useCloseOnNavigate(enabled: boolean) {
  const { pathname } = useLocation();
  const close = useOnboardingDialogStore((state) => state.close);
  const previousPathRef = useRef(pathname);

  useEffect(() => {
    if (previousPathRef.current === pathname) {
      return;
    }
    previousPathRef.current = pathname;
    if (enabled) {
      close();
    }
  }, [pathname, enabled, close]);
}

function OnboardingDialogContent() {
  const isOpen = useOnboardingDialogStore((state) => state.isOpen);
  const sessionKey = useOnboardingDialogStore((state) => state.sessionKey);
  const close = useOnboardingDialogStore((state) => state.close);
  const title = useAssistantPanelTitle();
  const prefersReducedMotion = useReducedMotion();
  const { isMobile } = useScreenSize();

  useCloseOnNavigate(isMobile);

  // Render mode comes from constance (via the onboarding state endpoint): "docked" is a right-side
  // panel that the page makes room for; "window" is the centered dialog. Preload it (not gated on
  // isOpen) so the layout is known before the dialog opens — otherwise a docked chat would briefly
  // flash the window mode while the request resolved.
  const { data: onboardingState, isLoading: isLayoutLoading } = useOnboardingState();
  const isDocked = onboardingState?.layout === 'docked';

  useAutoOpenOnboarding({ enabled: true, onboardingState });

  // The checklist and the conversation are the same wherever they land; only the frame around them
  // and the control that dismisses it differ.
  const body = (leadingAction: ReactNode) => (
    <ChecklistPane>
      {({ list, isListOpen, onAsk }) => (
        <OnboardingConversation
          key={sessionKey}
          list={list}
          isListOpen={isListOpen}
          onAsk={onAsk}
          leadingAction={leadingAction}
        />
      )}
    </ChecklistPane>
  );

  // Hold off rendering a container until the layout is resolved, so a docked chat never flashes the
  // window mode first. Preloaded above, so this is settled before the user can open the dialog.
  if (isLayoutLoading) {
    return null;
  }

  // Both catalog layouts collapse to one sheet on a phone: a side column and a 720px dialog each
  // want room a phone doesn't have. The sheet carries the title and the dismiss itself, so the
  // panel header and the control on the panel's edge both go.
  if (isMobile) {
    return (
      <SwipeSheet flush className="h-[90dvh]" closeLabel={HIDE_LABEL} open={isOpen} title={title} onClose={close}>
        {body(null)}
      </SwipeSheet>
    );
  }

  const panel = (
    <div className="flex h-full min-h-0 flex-col">
      <div className="flex flex-none items-center gap-3 border-grey30 border-b px-6 py-3.5">
        <Typography className="min-w-0 flex-1 font-bold text-[1.375rem] text-grey90 leading-tight">{title}</Typography>
      </div>
      {body(
        <Tooltip title={HIDE_LABEL} placement="top">
          <IconButton
            aria-label={HIDE_LABEL}
            onClick={close}
            sx={{
              // Centred on the panel's left edge, so half of it sits over the page.
              position: 'absolute',
              left: -16,
              top: '50%',
              transform: 'translateY(-50%)',
              width: 32,
              height: 32,
              padding: 0,
              color: 'grey.600',
              backgroundColor: 'common.white',
              border: '1px solid',
              borderColor: 'grey.300',
              '&:hover': { borderColor: 'grey.400', backgroundColor: 'common.white' },
            }}
          >
            {isDocked ? <ChevronDoubleRightIcon width={20} height={20} /> : <CloseIcon width={18} height={18} />}
          </IconButton>
        </Tooltip>,
      )}
    </div>
  );

  if (isDocked) {
    // AnimatePresence keeps the panel mounted through its exit, so closing slides it off the right
    // edge instead of blinking out.
    return (
      <AnimatePresence>
        {isOpen && (
          <motion.div
            role="dialog"
            aria-label={title}
            className="assistant-panel-elevation fixed right-0 bottom-0 z-[1300] flex w-assistant-panel flex-col border-grey30 border-l bg-white"
            // Starts below the fixed page banners, so nav, page and panel share one top edge.
            style={{ top: `var(${PAGE_BANNERS_HEIGHT_VAR}, 0px)` }}
            initial={{ x: '100%' }}
            animate={{ x: 0 }}
            exit={{ x: '100%' }}
            transition={prefersReducedMotion ? { duration: 0 } : SLIDE}
          >
            {panel}
          </motion.div>
        )}
      </AnimatePresence>
    );
  }

  return (
    <Dialog
      open={isOpen}
      onClose={close}
      aria-label={title}
      maxWidth={false}
      PaperProps={{ sx: { width: '100%', maxWidth: 720, height: '85vh', borderRadius: '12px' } }}
    >
      {panel}
    </Dialog>
  );
}
